import React, { useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import ReCAPTCHA from 'react-google-recaptcha';
import axios from 'axios';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useUiComponentsInternationalContext } from '@repo/static-translations';
import { LOCALE_REPLACEMENT_FOR_RECAPTCHA } from '@repo/gatsby-localization-helpers';
import { Button } from '@repo/ui-components/shadcn/Button/index';
import { localeToKlaviyoPublicMap } from '@repo/utils/klaviyo';

export default function ContactForm() {
  const recaptchaRef = useRef<ReCAPTCHA | null>(null);
  const { tt, locale } = useUiComponentsInternationalContext();
  const recaptchaLocale = LOCALE_REPLACEMENT_FOR_RECAPTCHA?.[locale] ?? locale;

  const [submitButtonDisabled, setSubmitButtonDisabled] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | null>(
    null
  );

  const validationSchema = useMemo(
    () =>
      yup.object({
        firstName: yup
          .string()
          .required(
            tt(
              s => s.contactFormDeptComponent.firstNameRequiredValidationMessage
            )
          )
          .min(
            2,
            tt(s => s.contactFormDeptComponent.firstNameValidationMessage)
          ),
        lastName: yup.string().test(
          'len',
          tt(s => s.contactFormDeptComponent.lastNameValidationMessage),
          val => val.length === 0 || val.length > 1
        ),
        email: yup
          .string()
          .email(tt(s => s.contactFormDeptComponent.emailValidationMessage))
          .required(
            tt(s => s.contactFormDeptComponent.emailRequiredValidationMessage)
          ),
        outlet: yup.string().test(
          'len',
          tt(s => s.contactFormDeptComponent.outletNameValidationMessage),
          val => val.length === 0 || val.length > 1
        ),
        message: yup
          .string()
          .required(
            tt(s => s.contactFormDeptComponent.messageRequiredValidationMessage)
          )
          .min(
            15,
            tt(s => s.contactFormDeptComponent.messageLengthValidationMessage)
          ),
      }),
    [tt]
  );

  type FormData = {
    firstName: string;
    lastName: string;
    email: string;
    outlet: string;
    message: string;
  };

  const {
    handleSubmit,
    register,
    formState: { errors },
    reset,
  } = useForm<FormData>({
    resolver: yupResolver(validationSchema),
  });

  async function onSubmit(data, e) {
    e.preventDefault();
    setSubmitButtonDisabled(true);

    const gRecaptchaResponse = recaptchaRef.current
      ? await recaptchaRef.current.execute()
      : null;

    if (!gRecaptchaResponse) return;

    try {
      const { firstName, lastName, email, outlet, message } = data;
      const outletLabel = outlet ? `${outlet}` : '';

      await axios.request({
        url: 'https://a.klaviyo.com/client/subscriptions/',
        params: {
          company_id: localeToKlaviyoPublicMap(
            locale,
            process.env.GATSBY_KLAVIYO_BRAND
          ).publicKey,
        },
        method: 'POST',
        headers: {
          accept: 'application/json',
          revision: '2025-07-15',
          'content-type': 'application/json',
        },
        data: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              custom_source: 'Press Inquries',
              profile: {
                data: {
                  type: 'profile',
                  attributes: {
                    first_name: firstName,
                    last_name: lastName,
                    email,
                    properties: {
                      outlet: outletLabel,
                      message,
                    },
                  },
                },
              },
            },
            relationships: {
              list: {
                data: {
                  type: 'list',
                  id: process.env.GATSBY_KLAVIYO_PRESS_INQUIRY_LIST_ID,
                },
              },
            },
          },
        }),
      });

      setSubmitStatus('success');
      setSubmitButtonDisabled(false);
      reset();
    } catch (error) {
      console.error('There was an error submitting the form:', error);
      setSubmitStatus('error');
      setSubmitButtonDisabled(false);
    }
  }

  return (
    <>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="w-full rounded-xl shadow-[0px_4px_56px_0px_rgba(5,37,77,0.15)] p-6 bg-white"
      >
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:space-x-4 space-y-4 sm:space-y-0">
            <div className="flex-1">
              <label
                htmlFor="firstName"
                className="block font-bold mb-1 text-sm leading-[16.8px]"
              >
                {tt(s => s.contactFormDeptComponent.firstNameLabel)}
              </label>
              <input
                id="firstName"
                type="text"
                {...register('firstName')}
                className={`w-full border ${
                  errors.firstName ? 'border-red-500' : 'border-gray-200'
                } p-2 rounded-lg`}
              />
              {errors.firstName && (
                <p className="relative top-1 text-red-500 text-sm">
                  {errors.firstName.message}
                </p>
              )}
            </div>
            <div className="flex-1">
              <label
                htmlFor="lastName"
                className="block font-bold mb-1 text-sm leading-[16.8px]"
              >
                {tt(s => s.contactFormDeptComponent.lastNameLabel)}
              </label>
              <input
                id="lastName"
                type="text"
                {...register('lastName')}
                className={`w-full border ${
                  errors.lastName ? 'border-red-500' : 'border-gray-200'
                } p-2 rounded-lg`}
              />
              {errors.lastName && (
                <p className="relative top-1 text-red-500 text-sm">
                  {errors.lastName.message}
                </p>
              )}
            </div>
          </div>
          <div className="flex flex-col sm:flex-row sm:space-x-4 space-y-4 sm:space-y-0">
            <div className="flex-1">
              <label
                htmlFor="email"
                className="block font-bold mb-1 text-sm leading-[16.8px]"
              >
                {tt(s => s.contactFormDeptComponent.emailAddressLabel)}
              </label>
              <input
                id="email"
                type="text"
                {...register('email')}
                className={`w-full border ${
                  errors.email ? 'border-red-500' : 'border-gray-200'
                } p-2 rounded-lg`}
              />
              {errors.email && (
                <p className="relative top-1 text-red-500 text-sm">
                  {errors.email.message}
                </p>
              )}
            </div>
            <div className="flex-1">
              <label
                htmlFor="outlet"
                className="block font-bold mb-1 text-sm leading-[16.8px]"
              >
                {tt(s => s.contactFormDeptComponent.outletNameLabel)}
              </label>
              <input
                type="text"
                {...register('outlet')}
                className={`w-full border ${
                  errors.outlet ? 'border-red-500' : 'border-gray-200'
                } p-2 rounded-lg`}
              />
              {errors.outlet && (
                <p className="relative top-1 text-red-500 text-sm">
                  {errors.outlet.message}
                </p>
              )}
            </div>
          </div>
          <div>
            <label
              htmlFor="message"
              className="block font-bold mb-1 text-sm leading-[16.8px]"
            >
              {tt(s => s.contactFormDeptComponent.messageLabel)}
            </label>
            <textarea
              id="message"
              rows={6}
              {...register('message')}
              className={`w-full resize-none border ${
                errors.message ? 'border-red-500' : 'border-gray-200'
              } p-2 rounded-lg`}
            />
            {errors.message && (
              <p className="relative top-1 text-red-500 text-sm">
                {errors.message.message}
              </p>
            )}
          </div>
          <div className="pt-2 space-y-4">
            {process.env.GATSBY_PETSAFE_RECAPTCHA_SITE_KEY && (
              <ReCAPTCHA
                ref={recaptchaRef}
                size="invisible"
                badge="inline"
                sitekey={process.env.GATSBY_PETSAFE_RECAPTCHA_SITE_KEY}
                hl={recaptchaLocale}
              />
            )}
            <Button
              variant="secondary"
              className="flex py-block px-inline gap-2 w-auto h-11 max-w-[83px]"
              type="submit"
              disabled={submitButtonDisabled}
            >
              {tt(s => s.contactFormDeptComponent.submitButton)}
            </Button>
          </div>
        </div>
      </form>
      {submitStatus === 'success' && (
        <p className="text-green-600 text-sm">
          Your message was sent successfully!
        </p>
      )}
      {submitStatus === 'error' && (
        <p className="text-red-600 text-sm">
          Something went wrong. Please try again.
        </p>
      )}
    </>
  );
}
